import React, { useEffect, useState } from 'react';
import { deliveryAPI, CardStepData, BinsPricing } from '../../../services/deliveryAPI';
import { useHelcimAddCard } from '../../../components/BridgeClub/payments/useHelcimAddCard';
import { parseApiError } from '../../../utils/apiErrors';

const RED = '#B91C1C';
const GREEN = '#065F46';

// The card is authorised (not charged) here. This is the sentence the customer agrees to
// by adding a card; it MUST match consent_text_version 'delivery-recurring-v1' on the
// server (delivery/onboarding_views.py).
const CONSENT_TEXT =
  "By adding your card you're authorising Red Bridge Farm Feed Co. to charge it for your " +
  "feed deliveries. We'll email you before each delivery with what's coming and what it " +
  "costs, and you can change or cancel it before we charge. Nothing is charged today.";

interface Props {
  onBack: () => void;
  onDone: () => void;
}

/**
 * Onboarding step 5 — service tier + card. Concierge is the default with Doorstep one tap
 * away. Shows what a delivery COSTS (real per-bag Treasure Valley prices from the same
 * bins-pricing endpoint step 3 used + the concierge fee), never a prediction of the first
 * order. Nothing is charged today; the pre-delivery email is where the order is agreed.
 * The HelcimPay add-card flow is the shared hook (also used on the profile page).
 */
const CardStep: React.FC<Props> = ({ onBack, onDone }) => {
  const [data, setData] = useState<CardStepData | null>(null);
  const [pricing, setPricing] = useState<BinsPricing | null>(null);
  const [tier, setTier] = useState<string>('full');
  const [loading, setLoading] = useState(true);
  const [savingTier, setSavingTier] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const [card, price] = await Promise.all([
      deliveryAPI.getCard(),
      deliveryAPI.getBinsPricing(),
    ]);
    setData(card);
    setPricing(price);
    setTier(card.service_tier || 'full');
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch {
        setError('We could not load this step. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Adding a card is the authorisation moment: stamp consent + persist the tier, then the
  // card list reflects the new card.
  const { startAddCard, adding } = useHelcimAddCard({
    onAdded: async () => {
      const updated = await deliveryAPI.patchCard({ service_tier: tier, consent: true });
      setData(updated);
    },
    onError: (m) => setError(m || null),
  });

  const chooseTier = async (value: string) => {
    if (value === tier || savingTier) return;
    const previous = tier;
    setTier(value);                 // optimistic — the toggle must feel instant
    setSavingTier(true);
    setError(null);
    try {
      const updated = await deliveryAPI.patchCard({ service_tier: value });
      setData(updated);
    } catch {
      setTier(previous);
      setError('We could not save that choice. Please try again.');
    } finally {
      setSavingTier(false);
    }
  };

  const complete = async () => {
    if (!data?.has_card || completing) return;
    setCompleting(true);
    setError(null);
    try {
      await deliveryAPI.patchCard({ service_tier: tier, consent: true });
      await deliveryAPI.complete();
      onDone();
    } catch (e: any) {
      setError(parseApiError(e, {
        fallback: 'We could not finish. Please try again.',
      }).message);
      setCompleting(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>Loading…</div>;
  }

  const quotedBins = (pricing?.bins || []).filter((b) => b.selected && b.deliverable);
  const tierOptions = data?.tier_options || [];

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 16px 40px' }}>
      <h2 style={{ color: RED, marginBottom: 4 }}>Your service and card</h2>
      <p style={{ color: '#555', marginTop: 0 }}>
        Choose how we deliver, then add a card. Nothing is charged today.
      </p>
      <p style={{ color: '#555', marginTop: 0, fontSize: 14 }}>
        Your bank may show a $0 or $1 card check for a few days. It is not a charge and
        disappears on its own. Nothing is charged to your card until your first delivery.
      </p>

      {error && (
        <div style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5',
          borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* ── Service tier: Concierge default, Doorstep one tap away ───────────── */}
      <h3 style={{ marginBottom: 8 }}>How we deliver</h3>
      <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
        {tierOptions.map((opt) => {
          const active = tier === opt.value;
          const free = opt.first_visit_free;
          const feeNum = Number(opt.fee);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => chooseTier(opt.value)}
              disabled={savingTier}
              aria-pressed={active}
              style={{
                flex: 1, textAlign: 'left', cursor: savingTier ? 'default' : 'pointer',
                border: `2px solid ${active ? RED : '#D1D5DB'}`,
                background: active ? '#FFF5F5' : '#fff',
                borderRadius: 12, padding: '14px 14px',
                boxShadow: active ? '0 1px 6px rgba(185,28,28,0.15)' : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 800, color: active ? RED : '#111' }}>{opt.label}</span>
                <span style={{ width: 18, height: 18, borderRadius: '50%',
                  border: `2px solid ${active ? RED : '#9CA3AF'}`, background: active ? RED : '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 12 }}>{active ? '✓' : ''}</span>
              </div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>
                {feeNum > 0
                  ? <>${opt.fee} a visit{free && <span style={{ color: GREEN, fontWeight: 700 }}> · first visit free</span>}</>
                  : <>No visit fee — we leave it at your door</>}
              </div>
            </button>
          );
        })}
      </div>
      <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 20px' }}>
        Concierge brings it in and checks your bins. Switch to Doorstep any time.
      </p>

      {/* ── What a delivery costs (real per-bag prices, never a prediction) ──── */}
      <h3 style={{ marginBottom: 8 }}>What a delivery costs</h3>
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
        {quotedBins.length === 0 ? (
          <div style={{ padding: 14, color: '#6B7280', fontSize: 14 }}>
            No feed selected yet.
          </div>
        ) : quotedBins.map((b) => (
          <div key={b.bin_id} style={{ display: 'flex', justifyContent: 'space-between',
            padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 14 }}>
            <span style={{ color: '#111' }}>{b.product_name}</span>
            <span style={{ color: '#111', fontWeight: 700 }}>${b.unit_price} / bag</span>
          </div>
        ))}
        {tier === 'full' && (
          <div style={{ display: 'flex', justifyContent: 'space-between',
            padding: '12px 14px', fontSize: 14, background: '#FafafA' }}>
            <span style={{ color: '#111' }}>Concierge service</span>
            <span style={{ color: '#111', fontWeight: 700 }}>
              $15 / visit <span style={{ color: GREEN }}>· first free</span>
            </span>
          </div>
        )}
      </div>
      <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 20px' }}>
        These are per-bag prices, not a total — {data?.first_delivery_notice}
      </p>

      {/* ── Card ─────────────────────────────────────────────────────────────── */}
      <h3 style={{ marginBottom: 8 }}>Payment card</h3>
      {data?.cards && data.cards.length > 0 ? (
        <div style={{ marginBottom: 10 }}>
          {data.cards.map((c) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
              border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <span style={{ fontSize: 20 }}>💳</span>
              <span style={{ fontWeight: 700 }}>
                {(c.card_type || 'Card')} •••• {c.last_four || '????'}
              </span>
              {c.card_expiry && <span style={{ color: '#6B7280', fontSize: 13 }}>Exp {c.card_expiry}</span>}
              {c.is_default && (
                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: GREEN }}>Default</span>
              )}
            </div>
          ))}
          <button type="button" onClick={() => { setError(null); startAddCard(); }} disabled={adding}
            style={{ background: 'none', border: 'none', color: RED, fontWeight: 700,
              cursor: adding ? 'default' : 'pointer', padding: 0, fontSize: 14 }}>
            {adding ? 'Starting…' : '+ Use a different card'}
          </button>
        </div>
      ) : (
        <>
          <button type="button" onClick={() => { setError(null); startAddCard(); }} disabled={adding}
            style={{ width: '100%', padding: '14px', border: `2px dashed ${RED}`,
              borderRadius: 12, background: '#fff', color: RED, fontWeight: 800,
              cursor: adding ? 'default' : 'pointer', marginBottom: 10 }}>
            {adding ? 'Starting…' : '+ Add a card'}
          </button>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 6px' }}>
            Card details are entered securely with Helcim — we never see or store your card number.
          </p>
        </>
      )}

      <p style={{ fontSize: 12, color: '#4B5563', background: '#F9FAFB', border: '1px solid #E5E7EB',
        borderRadius: 8, padding: '10px 12px', margin: '10px 0 20px', lineHeight: 1.5 }}>
        {CONSENT_TEXT}
      </p>

      {/* ── Nav ──────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button type="button" onClick={onBack}
          style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 14 }}>
          ← Back
        </button>
        <button type="button" onClick={complete} disabled={!data?.has_card || completing}
          style={{ padding: '12px 22px', border: 'none', borderRadius: 8,
            background: (!data?.has_card || completing) ? '#9CA3AF' : RED, color: '#fff',
            fontWeight: 700, cursor: (!data?.has_card || completing) ? 'default' : 'pointer' }}>
          {completing ? 'Finishing…' : 'Complete subscription'}
        </button>
      </div>
      {!data?.has_card && (
        <p style={{ textAlign: 'right', color: '#b45309', fontSize: 13, marginTop: 8 }}>
          Add a card to finish.
        </p>
      )}
    </div>
  );
};

export default CardStep;
